/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Chip,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Tooltip
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Search,
  ClearAll,
  Download,
  Code,
  Psychology,
  Build,
  CheckCircle,
  Error,
  HourglassEmpty,
  SmartToy,
  Settings
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import api from '../../services/api';

const TOKEN_EXAMINER_MODELS = [
  'All',
  'gemini-2.0-flash-exp',
  'gemini-1.5-pro',
  'gemini-1.5-flash'
];

const PASS_TYPES = [
  { value: 'All', label: 'All Pass Types' },
  { value: 'within_document', label: 'Within-Document' },
  { value: 'cross_document', label: 'Cross-Document' },
  { value: 'temporal', label: 'Temporal' }
];

const DIALOGUE_TYPE_CONFIG = {
  prompt: {
    icon: <Code fontSize="small" />,
    label: 'Prompt',
    color: 'primary',
    bgColor: 'primary.lighter',
    borderColor: 'primary.main',
    isHighValue: true
  },
  llm_thinking: {
    icon: <Psychology fontSize="small" />,
    label: 'LLM Thinking',
    color: 'secondary',
    bgColor: 'secondary.lighter',
    borderColor: 'secondary.main',
    isHighValue: true
  },
  tool_call: {
    icon: <Build fontSize="small" />,
    label: 'Tool Call',
    color: 'warning',
    bgColor: 'warning.lighter',
    borderColor: 'warning.main',
    isHighValue: true
  },
  tool_result: {
    icon: <CheckCircle fontSize="small" />,
    label: 'Tool Result',
    color: 'success',
    bgColor: 'success.lighter',
    borderColor: 'success.main',
    isHighValue: true
  },
  llm_response: {
    icon: <SmartToy fontSize="small" />,
    label: 'LLM Response',
    color: 'info',
    bgColor: 'info.lighter',
    borderColor: 'info.main',
    isHighValue: true
  },
  error: {
    icon: <Error fontSize="small" />,
    label: 'Error',
    color: 'error',
    bgColor: 'error.lighter',
    borderColor: 'error.main',
    isHighValue: true
  },
  metadata: {
    icon: <Settings fontSize="small" />,
    label: 'Chain Event',
    color: 'default',
    bgColor: 'action.hover',
    borderColor: 'divider',
    isHighValue: false  // Chain events are low-value noise by default
  }
};

const STATUS_ICONS = {
  running: <HourglassEmpty fontSize="small" color="warning" />,
  success: <CheckCircle fontSize="small" color="success" />,
  failed: <Error fontSize="small" color="error" />
};

// eslint-disable-next-line max-lines-per-function
function TokenExaminerTab({ entityId }) {
  const theme = useTheme();
  const [searchPanelExpanded, setSearchPanelExpanded] = useState(true);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedExecutions, setExpandedExecutions] = useState(new Set());
  const [showChainEvents, setShowChainEvents] = useState(false);  // Hide chain metadata by default

  // Filter state
  const [filters, setFilters] = useState({
    status: 'All',
    passType: 'All',
    documentId: '',
    model: 'All',
    startDate: '',
    endDate: ''
  });

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'All',
      passType: 'All',
      documentId: '',
      model: 'All',
      startDate: '',
      endDate: ''
    });
  };

  const handleSearch = async () => {
    if (!entityId) {
      setError('No entity selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiFilters = {};
      if (filters.status !== 'All') apiFilters.status = filters.status.toLowerCase();
      if (filters.passType !== 'All') apiFilters.passType = filters.passType;
      if (filters.documentId) apiFilters.documentId = filters.documentId;
      if (filters.model !== 'All') apiFilters.model = filters.model;
      if (filters.startDate) apiFilters.startDate = filters.startDate;
      if (filters.endDate) apiFilters.endDate = filters.endDate;

      const result = await api.getTeExecutions(entityId, apiFilters);
      setExecutions(result.executions || []);
    } catch (err) {
      console.error('Error fetching TE executions:', err);
      setError(err.message || 'Failed to fetch Token Examiner executions');
    } finally {
      setLoading(false);
    }
  };

  const toggleExecutionExpand = async (executionId) => {
    const newExpanded = new Set(expandedExecutions);

    if (newExpanded.has(executionId)) {
      newExpanded.delete(executionId);
    } else {
      newExpanded.add(executionId);

      const execution = executions.find(e => e.executionId === executionId);
      if (execution && !execution.dialogue) {
        try {
          const result = await api.getTeExecutionDialogue(entityId, executionId);
          setExecutions(executions.map(e =>
            e.executionId === executionId
              ? { ...e, dialogue: result.dialogue || [] }
              : e
          ));
        } catch (err) {
          console.error('Error loading dialogue:', err);
        }
      }
    }

    setExpandedExecutions(newExpanded);
  };

  const handleExport = () => {
    const timestamp = format(new Date(), 'yyyyMMdd-HHmmss');
    const filename = `te-dialogue-${entityId}-${timestamp}.json`;
    const blob = new Blob([JSON.stringify(executions, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (entityId) {
      handleSearch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  const safeFormatDate = (timestamp, formatString) => {
    if (!timestamp) return 'N/A';
    try {
      let date;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp._seconds !== undefined) {
        // Firestore Timestamp serialized as JSON
        date = new Date(timestamp._seconds * 1000);
      } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        return 'N/A';
      }

      if (isNaN(date.getTime())) {
        return 'N/A';
      }

      return format(date, formatString);
    } catch (err) {
      console.warn('Date formatting error:', err, timestamp);
      return 'N/A';
    }
  };

  // Get dialogue entry counts by type
  const getDialogueCounts = (dialogue) => {
    if (!dialogue) return {};
    return dialogue.reduce((acc, entry) => {
      acc[entry.type] = (acc[entry.type] || 0) + 1;
      return acc;
    }, {});
  };

  // Filter dialogue entries based on showChainEvents toggle
  const filterDialogue = (dialogue) => {
    if (!dialogue) return [];
    if (showChainEvents) return dialogue;
    return dialogue.filter(entry => {
      const config = DIALOGUE_TYPE_CONFIG[entry.type];
      return config?.isHighValue !== false;
    });
  };

  // Extract meaningful content from entry
  const extractContent = (entry) => {
    if (entry.type === 'tool_call') {
      return {
        main: entry.toolName ? `Calling tool: ${entry.toolName}` : 'Tool Call',
        detail: entry.toolArguments
          ? (typeof entry.toolArguments === 'string' ? entry.toolArguments : JSON.stringify(entry.toolArguments, null, 2))
          : null
      };
    }
    if (entry.type === 'tool_result') {
      const result = entry.toolResult || entry.content;
      return {
        main: 'Tool Result',
        detail: result ? (typeof result === 'string' ? result : JSON.stringify(result, null, 2)) : null
      };
    }
    if (entry.type === 'metadata' && entry.content) {
      const content = entry.content;
      if (content.event) {
        return {
          main: `${content.event}${content.chainName ? `: ${content.chainName}` : ''}`,
          detail: content.inputs || content.outputs
            ? JSON.stringify(content.inputs || content.outputs, null, 2)
            : null
        };
      }
    }
    return {
      main: null,
      detail: entry.content
        ? (typeof entry.content === 'string' ? entry.content : JSON.stringify(entry.content, null, 2))
        : null
    };
  };

  const renderDialogueEntry = (entry, index) => {
    const config = DIALOGUE_TYPE_CONFIG[entry.type] || DIALOGUE_TYPE_CONFIG.metadata;
    const { main, detail } = extractContent(entry);

    // Determine border color based on theme
    const getBorderColor = () => {
      switch (entry.type) {
        case 'error': return theme.palette.error.main;
        case 'llm_response': return theme.palette.info.main;
        case 'tool_result': return theme.palette.success.main;
        case 'tool_call': return theme.palette.warning.main;
        case 'prompt': return theme.palette.primary.main;
        case 'llm_thinking': return theme.palette.secondary.main;
        default: return theme.palette.divider;
      }
    };

    return (
      <Box
        key={index}
        sx={{
          p: 2,
          mb: 1,
          backgroundColor: entry.type === 'metadata'
            ? theme.palette.action.disabledBackground
            : theme.palette.action.hover,
          borderLeft: `4px solid ${getBorderColor()}`,
          borderRadius: 1,
          opacity: entry.type === 'metadata' ? 0.8 : 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ color: `${config.color}.main` }}>
            {config.icon}
          </Box>
          <Chip
            label={config.label}
            size="small"
            color={config.color === 'default' ? 'default' : config.color}
            variant={entry.type === 'metadata' ? 'outlined' : 'filled'}
            sx={{ fontWeight: 600 }}
          />
          {entry.toolName && entry.type === 'tool_call' && (
            <Chip
              label={entry.toolName}
              size="small"
              color="primary"
              variant="outlined"
              icon={<Build sx={{ fontSize: 14 }} />}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Step {entry.step} • {safeFormatDate(entry.timestamp, 'HH:mm:ss.SSS')}
          </Typography>
        </Box>

        {main && (
          <Typography variant="body2" sx={{ fontWeight: 500, mb: detail ? 1 : 0 }}>
            {main}
          </Typography>
        )}

        {detail && (
          <Box
            sx={{
              p: 1.5,
              backgroundColor: theme.palette.background.paper,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              maxHeight: entry.type === 'metadata' ? 150 : 300,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            {detail}
          </Box>
        )}

        {entry.error && (
          <Alert severity="error" sx={{ mt: 1 }} variant="outlined">
            {entry.error}
          </Alert>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SmartToy color="primary" />
        Token Examiner Dialogue
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper sx={{ mb: 3 }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            backgroundColor: 'action.hover'
          }}
          onClick={() => setSearchPanelExpanded(!searchPanelExpanded)}
        >
          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Search Filters
          </Typography>
          <IconButton size="small">
            {searchPanelExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={searchPanelExpanded}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={filters.status} onChange={handleFilterChange('status')} label="Status">
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Running">Running</MenuItem>
                    <MenuItem value="Success">Success</MenuItem>
                    <MenuItem value="Failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <FormControl fullWidth>
                  <InputLabel>Pass Type</InputLabel>
                  <Select value={filters.passType} onChange={handleFilterChange('passType')} label="Pass Type">
                    {PASS_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <FormControl fullWidth>
                  <InputLabel>Model</InputLabel>
                  <Select value={filters.model} onChange={handleFilterChange('model')} label="Model">
                    {TOKEN_EXAMINER_MODELS.map((model) => (
                      <MenuItem key={model} value={model}>
                        {model}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  label="Document ID"
                  value={filters.documentId}
                  onChange={handleFilterChange('documentId')}
                  placeholder="Filter by document ID"
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.startDate}
                  onChange={handleFilterChange('startDate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.endDate}
                  onChange={handleFilterChange('endDate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                disabled={loading}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearAll />}
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
              {executions.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleExport}
                  sx={{ ml: 'auto' }}
                >
                  Export to JSON
                </Button>
              )}
            </Box>
          </Box>
        </Collapse>
      </Paper>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && executions.length === 0 && (
        <Alert severity="info">
          No Token Examiner executions found matching the current filters.
        </Alert>
      )}
      {!loading && executions.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Found {executions.length} execution{executions.length !== 1 ? 's' : ''}
          </Typography>

          {/* eslint-disable-next-line max-lines-per-function */}
          {executions.map((execution) => {
            const isExpanded = expandedExecutions.has(execution.executionId);
            const statusIcon = STATUS_ICONS[execution.status] || STATUS_ICONS.running;

            return (
              <Card key={execution.executionId} sx={{ mb: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => toggleExecutionExpand(execution.executionId)}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {statusIcon}
                        <Typography variant="h6" sx={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>
                          {execution.executionId}
                        </Typography>
                        <Chip
                          label={execution.status}
                          size="small"
                          color={
                            execution.status === 'success' ? 'success' :
                            execution.status === 'failed' ? 'error' :
                            'warning'
                          }
                        />
                        <Chip
                          label={execution.passType.replace('_', '-')}
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      <Grid container spacing={2} sx={{ fontSize: '0.875rem' }}>
                        <Grid
                          size={{
                            xs: 12,
                            sm: 6,
                            md: 3
                          }}>
                          <Typography variant="caption" color="text.secondary">Document ID:</Typography>
                          <Typography variant="body2">{execution.documentId}</Typography>
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            sm: 6,
                            md: 3
                          }}>
                          <Typography variant="caption" color="text.secondary">Start Time:</Typography>
                          <Typography variant="body2">
                            {safeFormatDate(execution.startTime, 'PPpp')}
                          </Typography>
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            sm: 6,
                            md: 3
                          }}>
                          <Typography variant="caption" color="text.secondary">Duration:</Typography>
                          <Typography variant="body2">{formatDuration(execution.executionTimeMs)}</Typography>
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            sm: 6,
                            md: 3
                          }}>
                          <Typography variant="caption" color="text.secondary">Model:</Typography>
                          <Typography variant="body2">{execution.modelName || 'N/A'}</Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <IconButton>
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>

                  <Collapse in={isExpanded}>
                    <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                      {execution.dialogue && execution.dialogue.length > 0 ? (
                        <>
                          {/* Dialogue header with counts and filter toggle */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Dialogue Trace
                              </Typography>
                              {/* Entry type counts */}
                              {Object.entries(getDialogueCounts(execution.dialogue)).map(([type, count]) => {
                                const config = DIALOGUE_TYPE_CONFIG[type];
                                if (!config) return null;
                                return (
                                  <Tooltip key={type} title={`${count} ${config.label}${count !== 1 ? 's' : ''}`}>
                                    <Chip
                                      size="small"
                                      icon={config.icon}
                                      label={count}
                                      color={config.color === 'default' ? 'default' : config.color}
                                      variant="outlined"
                                      sx={{
                                        opacity: showChainEvents || config.isHighValue ? 1 : 0.5,
                                        fontSize: '0.75rem'
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={showChainEvents}
                                  onChange={(e) => setShowChainEvents(e.target.checked)}
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="caption" color="text.secondary">
                                  Show chain events
                                </Typography>
                              }
                            />
                          </Box>

                          {/* Filtered dialogue entries */}
                          <Box>
                            {filterDialogue(execution.dialogue).length > 0 ? (
                              filterDialogue(execution.dialogue).map((entry, index) => renderDialogueEntry(entry, index))
                            ) : (
                              <Alert severity="info">
                                All entries are chain events. Enable "Show chain events" to view them.
                              </Alert>
                            )}
                          </Box>
                        </>
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            Loading dialogue...
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default TokenExaminerTab;
